import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useSearchParams, useLocation } from 'react-router-dom'
import { useEffect, Suspense, lazy, useMemo } from 'react'
import Lenis from 'lenis'
// Keep Home eager-loaded to prioritize above-the-fold content and improve LCP.
import Home from '@pages/Home'

// Lazy load pages for code splitting
const AllTimePeak = lazy(() => import('@pages/AllTimePeak'))
const CurrentGrid2025 = lazy(() => import('@pages/CurrentGrid2025'))
const DriverProfile = lazy(() => import('@pages/DriverProfile'))
const Comparison = lazy(() => import('@pages/Comparison'))
const Timeline = lazy(() => import('@pages/Timeline'))
const Methodology = lazy(() => import('@pages/Methodology'))
const AboutModel = lazy(() => import('@pages/AboutModel'))
const FAQ = lazy(() => import('@pages/FAQ'))
const ComparisonSeoPage = lazy(() => import('@pages/ComparisonSeoPage'))
const AnalysisHub = lazy(() => import('@pages/AnalysisHub'))
const EditorialPage = lazy(() => import('@pages/EditorialPage'))

// Components
import Navbar from '@components/ui/Navbar'
import LoadingSpinner from '@components/ui/LoadingSpinner'
import StructuredData from '@components/seo/StructuredData'
import { buildBreadcrumbSchema, buildWebSiteSchema } from '@/utils/structuredData'


// Page loading fallback
const PageLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 'calc(100vh - 80px)',
    paddingTop: '80px'
  }}>
    <LoadingSpinner size="large" text="Loading F1 Elo archive..." />
  </div>
)

const LegacyDriverRedirect = () => {
  const { driverId } = useParams()
  const [searchParams] = useSearchParams()
  const query = searchParams.toString()
  const normalizedDriverId = String(driverId || '').replace(/_/g, '-')
  const destination = `/drivers/${normalizedDriverId}${query ? `?${query}` : ''}`

  return <Navigate to={destination} replace />
}

const BREADCRUMB_LABELS = {
  '': 'Home',
  'all-time': 'All-Time Elo Ratings',
  'all-time-elo-rankings': 'All-Time Elo Ratings',
  'current-grid': 'Current F1 Grid',
  comparison: 'Driver Comparison',
  timeline: 'Elo Timeline',
  methodology: 'Methodology',
  'about-model': 'About the Elo Model',
  faq: 'FAQ',
  compare: 'Driver Comparison',
  analysis: 'Analysis',
  drivers: 'Driver Profiles',
  driver: 'Driver Profile'
}

const formatBreadcrumbLabel = (segment, isLastSegment) => {
  if (BREADCRUMB_LABELS[segment]) return BREADCRUMB_LABELS[segment]
  const label = isLastSegment ? decodeURIComponent(segment).replace(/-/g, ' ') : segment
  return label.replace(/\b\w/g, char => char.toUpperCase())
}

const AppStructuredData = () => {
  const location = useLocation()
  const breadcrumbs = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean)
    const items = [{ name: 'Home', path: '/' }]
    let currentPath = ''
    segments.forEach((segment, index) => {
      currentPath += `/${segment}`
      items.push({
        name: formatBreadcrumbLabel(segment, index === segments.length - 1),
        path: currentPath
      })
    })
    return items
  }, [location.pathname])

  return (
    <>
      <StructuredData id="schema-website" data={buildWebSiteSchema()} />
      <StructuredData id="schema-breadcrumb" data={buildBreadcrumbSchema(breadcrumbs)} />
    </>
  )
}

const getRuntimeProfile = () => {
  if (typeof window === 'undefined') {
    return { enableSmoothScroll: false }
  }

  const prefersReducedMotion =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  const saveData = navigator.connection?.saveData ?? false
  const lowHardware =
    (navigator.hardwareConcurrency !== undefined && navigator.hardwareConcurrency <= 4) ||
    (navigator.deviceMemory !== undefined && navigator.deviceMemory <= 4)
  const isEdge = /\bEdg\//.test(navigator.userAgent)

  return {
    enableSmoothScroll: !prefersReducedMotion && !saveData && !lowHardware && !isEdge
  }
}

function App() {
  const runtimeProfile = useMemo(() => getRuntimeProfile(), [])

  // Initialize Lenis smooth scrolling
  useEffect(() => {
    if (!runtimeProfile.enableSmoothScroll) return undefined

    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    })

    let rafId
    function raf(time) {
      lenis.raf(time)
      rafId = requestAnimationFrame(raf)
    }

    rafId = requestAnimationFrame(raf)

    return () => {
      if (rafId !== undefined) cancelAnimationFrame(rafId)
      lenis.destroy()
    }
  }, [runtimeProfile.enableSmoothScroll])

  return (
    <Router>
      <AppStructuredData />
      {/* Main app content */}
      <div className="app">
        <Navbar />
        <main id="main-content" aria-label="F1 Elo content">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/all-time" element={<AllTimePeak />} />
              <Route path="/all-time-elo-rankings" element={<Navigate to="/all-time" replace />} />
              <Route path="/current-grid" element={<CurrentGrid2025 />} />
              <Route path="/drivers/:driverId" element={<DriverProfile />} />
              <Route path="/driver/:driverId" element={<LegacyDriverRedirect />} />
              <Route path="/comparison" element={<Comparison />} />
              <Route path="/compare/:pairSlug" element={<ComparisonSeoPage />} />
              <Route path="/timeline" element={<Timeline />} />
              <Route path="/methodology" element={<Methodology />} />
              <Route path="/about-model" element={<AboutModel />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/analysis" element={<AnalysisHub />} />
              <Route path="/analysis/:editorialSlug" element={<EditorialPage />} />
              {/* Redirect old leaderboard route to all-time */}
              <Route path="/leaderboard" element={<Navigate to="/all-time" replace />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    </Router>
  )
}

export default App
